import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Button, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useIntl } from 'react-intl'
import { organizationActions } from '../../_state/actions'
import { history } from '../../_state/helpers'
import { i18nMessages } from '../../Logic/CodeAccessible.i18n.Messages'
import CourseStatsScopeBar, { CourseStatsDateRange, resolveDateRange } from './CourseStatsScopeBar'
import CourseStatsHeadlineStrip from './CourseStatsHeadlineStrip'
import CourseStatsTable from './CourseStatsTable'
import CourseStatsDetailPanel from './CourseStatsDetailPanel'
import { organizationAllowsIndividualUserStatistics } from '../../Views/Private/CourseStatisticsConstants'

const useStyles = makeStyles(() => ({
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px',
    },
    findUsersButton: {
        marginTop: '28px',
    },
}))

/**
 * The course statistics body, shared by the organization and classroom pages. Scope is a prop,
 * not a branch: the two pages differ only by the classroom they pass in.
 *
 * Aggregate only. Every question of the form "which users are X" lives on the separate find users
 * page, which this links to - so the default view of the page answers "which courses are working"
 * without naming anybody.
 */
const CourseStatisticsContent = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const { organizationId, userGroupId } = props

    // Naming follows the dashboard vocabulary: the table lists topics, grouped by course.
    // API payload fields keep the backend spelling (courseGroupId, topicId, courses[] ...).
    const [dateRange, setDateRange] = useState(CourseStatsDateRange.All)
    const [selectedCourseGroupId, setSelectedCourseGroupId] = useState(null)
    const [selectedTopic, setSelectedTopic] = useState(null)

    const { fromDate, toDate } = resolveDateRange(dateRange)

    const overview = props.organization.courseStatsOverview
    const overviewLoading = props.organization.courseStatsOverviewLoading
    const trend = props.organization.courseStatsTopicTrend
    const trendLoading = props.organization.courseStatsTopicTrendLoading

    // Find users produces a named list of learners with their scores, which only school mode
    // organizations permit. Everything else on this page is aggregate and unaffected.
    const currentOrganizationDto = props.organization.myOrganizations?.find((x) => x.id === organizationId)
    const canFindUsers = organizationAllowsIndividualUserStatistics(currentOrganizationDto)

    // Depends on dateRange rather than the fromDate/toDate it derives, because resolveDateRange
    // returns fresh strings on every render and would otherwise refetch in a loop.
    useEffect(() => {
        if (!organizationId) {
            return
        }

        props.dispatch(
            organizationActions.getCourseStatsOverview(organizationId, {
                userGroupId,
                fromDate,
                toDate,
                courseGroupIds: selectedCourseGroupId ? [selectedCourseGroupId] : [],
            })
        )
    }, [organizationId, userGroupId, dateRange, selectedCourseGroupId])

    // Changing scope invalidates a selected topic, so clear it rather than leaving a detail panel
    // open under a new set of numbers.
    useEffect(() => {
        setSelectedTopic(null)
    }, [dateRange, selectedCourseGroupId, userGroupId])

    const handleSelectTopic = (topic) => {
        setSelectedTopic(topic)

        if (topic) {
            props.dispatch(
                organizationActions.getCourseStatsTopicTrend(organizationId, topic.topicId, {
                    userGroupId,
                    fromDate,
                    toDate,
                })
            )
        }
    }

    const findUsersPath = () =>
        userGroupId
            ? `/dashboard/classroom/${userGroupId}/statistics/courses/find-users`
            : '/dashboard/statistics/courses/find-users'

    // Scope travels in the query string so the find users page survives a refresh and can be
    // shared. Naming a topic implies its course, so both are carried.
    const handleFindUsersInTopic = (topic) => {
        const params = new URLSearchParams()
        if (topic?.courseGroupId) {
            params.set('courseGroupId', topic.courseGroupId)
        }
        if (topic?.topicId) {
            params.set('topicId', topic.topicId)
        }

        const query = params.toString()
        history.push(findUsersPath() + (query ? `?${query}` : ''))
    }

    const handleFindUsers = () => {
        const params = new URLSearchParams()
        if (selectedCourseGroupId) {
            params.set('courseGroupId', selectedCourseGroupId)
        }

        const query = params.toString()
        history.push(findUsersPath() + (query ? `?${query}` : ''))
    }

    const selectedTopicKey = selectedTopic ? `${selectedTopic.courseGroupId}_${selectedTopic.topicId}` : null

    return (
        <div>
            <CourseStatsScopeBar
                courseGroups={overview?.courseGroups}
                selectedCourseGroupId={selectedCourseGroupId}
                onCourseGroupChange={setSelectedCourseGroupId}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
            />

            {overviewLoading && (
                <div className={classes.loading}>
                    <CircularProgress />
                </div>
            )}

            {!overviewLoading && (
                <div>
                    <CourseStatsHeadlineStrip headline={overview?.headline} />

                    <CourseStatsTable
                        courses={overview?.courses}
                        selectedTopicKey={selectedTopicKey}
                        onSelectTopic={handleSelectTopic}
                    />

                    <CourseStatsDetailPanel
                        course={selectedTopic}
                        trend={trend}
                        loading={trendLoading}
                        onFindUsersInTopic={canFindUsers ? handleFindUsersInTopic : null}
                    />

                    {canFindUsers && (
                        <Button
                            className={classes.findUsersButton}
                            variant="outlined"
                            color="primary"
                            onClick={handleFindUsers}>
                            {intl.formatMessage(i18nMessages.courseStatisticsFilterTitle)}
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}

function mapStateToProps(state) {
    const { organization, user } = state
    return {
        organization,
        user,
    }
}

export default connect(mapStateToProps)(CourseStatisticsContent)
